/**
 * MetricsCard
 * Componente para mostrar una métrica con anillo de progreso circular.
 *
 * Visual:
 *  - Label arriba del círculo
 *  - CircularProgress (determinate, thick) más visual que LinearProgress
 *  - Color del progreso según umbral:
 *      < 60%  → success
 *      60-85% → warning
 *      > 85%  → error
 *  - Value centralizado dentro del círculo
 */
import { Box, CircularProgress, Stack, Typography } from '@mui/material';
import { formatBytes, formatPercent } from '@/types/metrics';

interface MetricsCardProps {
  label: string;
  value: number;
  max?: number;
  format?: 'percent' | 'bytes' | 'number';
  unit?: string;
  /** Tamaño del círculo (default 88) */
  size?: number;
}

function thresholdColor(pct: number): 'success' | 'warning' | 'error' {
  if (pct < 0.6) return 'success';
  if (pct < 0.85) return 'warning';
  return 'error';
}

export const MetricsCard: React.FC<MetricsCardProps> = ({
  label,
  value,
  max = 100,
  format = 'percent',
  unit = '',
  size = 88,
}) => {
  // Guard against missing data so the page doesn't blow up before the
  // first metric tick lands.
  const safeValue = typeof value === 'number' && Number.isFinite(value) ? value : 0;
  const safeMax = typeof max === 'number' && Number.isFinite(max) && max > 0 ? max : 100;

  let displayValue: string;
  let percentage: number;

  if (format === 'percent') {
    displayValue = formatPercent(safeValue);
    percentage = safeValue / 100;
  } else if (format === 'bytes' && max) {
    displayValue = `${formatBytes(safeValue)} / ${formatBytes(max)}`;
    percentage = safeValue / safeMax;
  } else {
    displayValue = `${safeValue.toFixed(1)}${unit}`;
    percentage = safeValue / safeMax;
  }

  const pct = Math.min(Math.max(percentage, 0), 1);
  const pctDisplay = Math.round(pct * 100);
  const color = thresholdColor(pct);

  return (
    <Box sx={{ mb: 2 }}>
      <Stack direction="row" alignItems="center" spacing={2}>
        <Box sx={{ position: 'relative', display: 'inline-flex', flexShrink: 0 }}>
          {/* Background track */}
          <CircularProgress
            variant="determinate"
            value={100}
            size={size}
            thickness={5}
            sx={{ color: (theme) => theme.palette.action.hover }}
          />
          {/* Foreground progress */}
          <CircularProgress
            variant="determinate"
            value={pctDisplay}
            size={size}
            thickness={5}
            color={color}
            sx={{
              position: 'absolute',
              left: 0,
              // Smooth transitions when value updates.
              transition: 'transform 200ms ease, color 200ms ease',
            }}
          />
          <Box
            sx={{
              top: 0,
              left: 0,
              bottom: 0,
              right: 0,
              position: 'absolute',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
            }}
          >
            <Typography
              variant="body2"
              fontWeight={700}
              sx={{ lineHeight: 1, fontSize: '0.95rem' }}
            >
              {pctDisplay}%
            </Typography>
          </Box>
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="subtitle2" gutterBottom sx={{ lineHeight: 1.2 }}>
            {label}
          </Typography>
          <Typography variant="body2" color="text.secondary" noWrap>
            {displayValue}
          </Typography>
        </Box>
      </Stack>
    </Box>
  );
};
